"use client";

/**
 * The person's engine pick ("Bash works on", Settings) for the surfaces that
 * shape a NEW task around it (engines T2b, founder 09-30: on Claude Code, the
 * person's own plan, the composer offers the Claude models alone, with no maker
 * column, and sends the pick).
 *
 * Read once per session from the same GET Settings reads, which never wakes the
 * computer, and written back by Settings on every read and pick, so a pick
 * reaches the composer without a reload. `null` = not known yet, or the engine
 * is not available to this person (the route 404s): the composer then behaves
 * exactly as before.
 *
 * Several accounts per engine: the same read carries each plan engine's
 * accounts while ENGINE_MULTI_ACCOUNT_V1 is on (per engine since T3), kept here
 * so the task view can name the account a task runs on, from its own engine's
 * list. `null` = not known, or the flag is off (no engine has a list): the task
 * view then shows nothing new.
 */

import { create } from "zustand";
import type { HarnessId } from "@bash/shared";
import { getEngineSettings, type Engine } from "@/lib/api/engine";
import { accountsOf, type EngineAccounts } from "@/lib/engine-plans";

interface EnginePreferenceState {
  engine: Engine | null;
  accounts: EngineAccounts | null;
  setEngine: (engine: Engine | null) => void;
  setAccounts: (accounts: EngineAccounts | null) => void;
}

export const useEnginePreferenceStore = create<EnginePreferenceState>(
  (set) => ({
    engine: null,
    accounts: null,
    setEngine: (engine) => set({ engine }),
    setAccounts: (accounts) => set({ accounts }),
  })
);

/** The engine the next task runs on, as the model gate reads it: Vestra's own until known. */
export function nextTaskEngine(): HarnessId {
  return useEnginePreferenceStore.getState().engine ?? "native";
}

let synced: Promise<void> | null = null;

/** The one read per session. A failed read is forgotten, so the next mount tries again. */
export function syncEnginePreference(): Promise<void> {
  synced ??= getEngineSettings()
    .then((status) => {
      const { setEngine, setAccounts } = useEnginePreferenceStore.getState();
      setEngine(status.available ? status.settings.engine : null);
      setAccounts(status.available ? accountsOf(status.settings) : null);
    })
    .catch(() => {
      synced = null;
    });
  return synced;
}

/** Test-only: a fresh session. */
export function __resetEnginePreferenceForTests(): void {
  synced = null;
  useEnginePreferenceStore.setState({ engine: null, accounts: null });
}
